import type { Responsive } from '#types/common'
import type { ResponsiveMedia } from '#types/content'
import type { CMSPicture, CMSVideo } from '#types/media'
import type { MediaMeta } from '#types/seo'

interface Image {
  src: Responsive
  width: Responsive<number>
  height: Responsive<number>
  alt: string
  meta: Partial<MediaMeta>
  aspectRatio: Responsive<number>
}

interface Video {
  src: Responsive
  width: Responsive<number>
  height: Responsive<number>
  poster: Responsive
  meta: Pick<MediaMeta, 'title' | 'description' | 'uploadDate'>
  aspectRatio: Responsive<number>
}

type MediaEntries<T> = [keyof Responsive, T][]

type ResponsiveMediaObject
  = { images: Image, aspectRatio: Responsive<number> }
    | { video: Video, aspectRatio: Responsive<number> }

/**
 * Get media URL for provided URL.
 *
 * It's needed because CoreMedia media URLs are not absolute.
 * We can get rid of this once we have full migration to Cloudinary.
 *
 * @param url - The media URL to process
 * @param baseUrl - Base URL to prepend if the URL is relative
 * @returns The absolute media URL
 */
export const getUrl = (url: string | undefined, baseUrl: string = ''): string => {
  if (!url) return ''
  return url.startsWith('http') ? url : `${baseUrl}${url}`
}

/**
 * Extract metadata from the first media entry.
 * @param media
 */
const getMeta = (media: MediaEntries<CMSPicture | CMSVideo>) => {
  const [[_, def = {} as CMSPicture | CMSVideo]] = media

  return {
    alt: (def as CMSPicture).alt,
    meta: {
      title: def.seoTitle,
      description: def.seoDescription,
      uploadDate: (def as CMSVideo).uploadDate
    }
  }
}

/**
 * Generates responsive image sources based on the provided images and URL transformation function.
 *
 * @param images - The array of responsive image entries.
 * @param baseUrl - Base media url.
 * @returns A responsive image object with transformed URLs and metadata.
 * @category Utils
 */
const getPicture = (images: MediaEntries<CMSPicture>, baseUrl?: string): Image => ({
  ...images.reduce((acc, [br, image]) => {
    acc.src[br] = getUrl(image.url, baseUrl)
    acc.width[br] = image.width
    acc.height[br] = image.height
    acc.aspectRatio[br] = image.width / image.height
    return acc
  }, { src: {}, width: {}, height: {}, aspectRatio: {} } as Pick<Image, 'src' | 'width' | 'height' | 'aspectRatio'>),
  ...getMeta(images)
})

/**
 * Generates responsive video sources based on the provided videos and URL transformation function.
 *
 * @param videos - The array of responsive video entries.
 * @param baseUrl - Base media url.
 * @returns A responsive object containing transformed video sources with metadata.
 * @category Utils
 */
const getVideo = (videos: MediaEntries<CMSVideo>, baseUrl?: string): Video => {
  const { meta } = getMeta(videos)

  const obj = videos.reduce((acc, [br, video]) => ({
    ...acc,
    [br]: {
      src: getUrl(video.url, baseUrl),
      width: video.width,
      height: video.height,
      poster: video.posterUrl,
      tracks: video.tracks,
      aspectRatio: video.width / video.height,
    }
  }), {})

  return {
    ...swapObjectKeys(obj),
    meta
  } as Video
}

/**
 * Map CMS Media response to base components props structure
 * @param media {ResponsiveMedia}
 * @param baseUrl {string}
 * @category Utils
 */
export const getResponsiveMedia = (
  media: ResponsiveMedia | undefined = {} as any,
  baseUrl?: string,
): ResponsiveMediaObject | any => {
  if (media === null) return {} as ResponsiveMediaObject

  const defaultMedia = media.sm || media.md || media.lg
  const mediaType = defaultMedia?.type

  if (!mediaType) return {} as ResponsiveMediaObject

  const mediaEntries = Object.entries(media)
    .filter(([_, entry]) => entry?.type === mediaType)

  if (mediaType === 'CMPicture') {
    const images = getPicture(mediaEntries as MediaEntries<CMSPicture>, baseUrl)

    return {
      images,
      aspectRatio: images.aspectRatio
    }
  }

  if (mediaType === 'CMVideo') {
    const video = getVideo(mediaEntries as MediaEntries<CMSVideo>, baseUrl)

    return {
      video,
      aspectRatio: video.aspectRatio
    }
  }

  return {} as ResponsiveMediaObject
}
